import IListAllSolarPlantsRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/list-all-solar-plants-response-dto";
import CountBillingSpacesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/count-billing-spaces-response-dto";
import CountConsumersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/count-consumers-response-dto";
import GetDeviceTypesByDevicePowerConsumptionResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-device-types-by-device-power-consumption-response-dto";
import GetDeviceTypesByTotalAveragePowerUsageResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-device-types-by-total-average-power-usage-response-dto";
import IListAllSolarPlantsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/list-all-solar-plants-response-dto";
import IViewCustomerSolarPlantDetailsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-details-response-dto";
import ViewHighestConsumedSpaceClustersOfLastMonthResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-highest-consumed-space-clusters-off-last-month-response-dto";
import ViewServiceProviderPreviousConsumedEnergyResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-service-provider-previous-consumed-energy-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { IViewTotalSolarPlantEnergyResponseDTO } from "../../../shared/oversight-core/dtos/response-dtos/view-total-solar-plant-energy-response-dto";
import { EDateTypes } from "../../../shared/oversight-core/enums/date-types";
import { EAlertSeverity } from "../../../shared/oversight-core/enums/alert-severity";
import { EAlertState } from "../../../shared/oversight-core/enums/alert-state";
import { ESolarPlantSize } from "../../../shared/oversight-core/enums/solar-plant-size";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { rootApi } from "../apiManager";
import { toQueryString } from "../query-string";

export const adminDashboardAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getHighestConsumptions: builder.query<
      ViewHighestConsumedSpaceClustersOfLastMonthResponseDTO,
      { limit: number }
    >({
      transformErrorResponse,
      query(limit) {
        return {
          url: `/admin/space-cluster/consumption/actual/last-month/view`,
          method: "GET",
          params: limit,
        };
      },
    }),
    getCountConsumers: builder.query<CountConsumersResponseDTO, void>({
      transformErrorResponse,
      query() {
        return {
          url: `/admin/customer/count`,
          method: "GET",
        };
      },
    }),
    getCountBillingSpaces: builder.query<CountBillingSpacesResponseDTO, void>({
      transformErrorResponse,
      query() {
        return {
          url: `/admin/space-cluster/count`,
          method: "GET",
        };
      },
    }),
    getTotalAveragePowerUsage: builder.query<
      GetDeviceTypesByTotalAveragePowerUsageResponseDTO,
      void
    >({
      transformErrorResponse,
      query() {
        return {
          url: `/admin/power-consumer/by-device-count/sort`,
          method: "GET",
        };
      },
    }),
    getActualConsumptions: builder.query<
      ViewServiceProviderPreviousConsumedEnergyResponseDTO,
      { monthLimit: number }
    >({
      transformErrorResponse,
      query(monthLimit) {
        return {
          url: `/admin/service-provider/consumed-energy/previous/view`,
          method: "GET",
          params: monthLimit,
        };
      },
    }),
    getDeviceTypesByDevicePowerConsumption: builder.query<
      GetDeviceTypesByDevicePowerConsumptionResponseDTO,
      { limit?: number }
    >({
      transformErrorResponse,
      query(limit) {
        return {
          url: `/admin/power-consumer/highest-power-consumption/sort`,
          method: "GET",
          params: limit,
        };
      },
    }),
    viewSolarPlantStatisticsAdmin: builder.query<
      IViewCustomerSolarPlantDetailsResponseDTO,
      void
    >({
      transformErrorResponse,
      query() {
        return {
          url: `/admin/generation-service-provider/statistics`,
          method: "GET",
        };
      },
    }),
    getPaginatedCustomerSolarPlantListAdmin: builder.query<
      IListAllSolarPlantsResponseDTO,
      IListAllSolarPlantsRequestDTO
    >({
      transformErrorResponse,
      query(queryParam) {
        return {
          url: `/admin/customer/solar-plant/list`,
          method: "GET",
          params: { ...queryParam },
        };
      },
    }),
    viewTotalPlantGeneration: builder.query<
      IViewTotalSolarPlantEnergyResponseDTO,
      {
        startDate?: string;
        endDate?: string;
        viewType?: EDateTypes;
      }
    >({
      transformErrorResponse,
      query({ startDate, endDate, viewType }) {
        return {
          url: `admin/generation-service-provider/energy-generation/view`,
          method: "GET",
          params: { startDate, endDate, viewType },
        };
      },
    }),
    viewSolarPlantStatuses: builder.query<
      ViewSolarPlantStatusesResponseDTO,
      {
        extended?: boolean;
        searchValue?: string;
        generationServiceProviderId?: string;
        solarPlantSize?: ESolarPlantSize;
        severity?: EAlertSeverity[];
        alertState?: EAlertState;
        alertName?: string[];
        from?: string;
        to?: string;
        plantId?: string;
      }
    >({
      transformErrorResponse,
      query({
        extended,
        searchValue,
        generationServiceProviderId,
        solarPlantSize,
        severity,
        alertState,
        alertName,
        from,
        to,
        plantId,
      }) {
        return {
          url: `admin/generation-service-provider/statuses${toQueryString({
            extended,
            searchValue,
            generationServiceProviderId,
            solarPlantSize,
            severity,
            alertState,
            alertName,
            from,
            to,
            plantId,
          })}`,
          method: "GET",
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetHighestConsumptionsQuery,
  useLazyGetCountConsumersQuery,
  useLazyGetCountBillingSpacesQuery,
  useLazyGetTotalAveragePowerUsageQuery,
  useLazyGetActualConsumptionsQuery,
  useLazyGetDeviceTypesByDevicePowerConsumptionQuery,
  useLazyViewSolarPlantStatisticsAdminQuery,
  useLazyGetPaginatedCustomerSolarPlantListAdminQuery,
  useGetPaginatedCustomerSolarPlantListAdminQuery,
  useLazyViewTotalPlantGenerationQuery,
  useLazyViewSolarPlantStatusesQuery,
} = adminDashboardAPI;
